import React, { useContext, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Container } from '@material-ui/core';
import { Hub, Logger } from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import { Authenticator } from '../../components/AwsAmplify';
import { AUTH_STATES } from '../../constants/authConsts';
import history from '../../history';
import { RouteContext, ContextProps } from '../../context';
import autoSignIn, { AutoSignInProps } from '../../utils/AutoSignIn';
import { LoginForm } from '../../components/Auth/Login';
import { ForgotPasswordForm } from '../../components/Auth/ForgotPassword';
import { ConfirmRegisterForm } from '../../components/Auth/ConfirmRegister';
import '../../components/Layout/Layout.css';
import { updateUserId } from '../../store/user/actions';
import {
  alertSnackbar,
  clearAlertMessage,
  toggleAutoSignIn,
} from '../../store/ui/actions';
import * as Colors from '../../theme/colors';
import { LoaderContainer } from '../../components/Loading';
import { RootState } from '../../store';
import { RegisterForm } from '../../components/Auth/Register';
import { RequireNewPasswordForm } from '../../components/Auth/RequireNewPassword';
import { UserErrorMessage } from '../../constants/errorConsts/errorCodesConsts';
import { OnboardingPageQueryParams } from '../../components/Onboarding/onboardingTypes';

const logger = new Logger('AuthLayout');

const useStyles = makeStyles(theme => ({
  '@global': {
    '*': {
      margin: 0,
      padding: 0,
    },
    html: {
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale',
      height: '100%',
      width: '100%',
    },
    body: {
      height: '100%',
      width: '100%',
    },
    '#root': {
      height: '100%',
      width: '100%',
    },
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontFamily: ['"Inter"', '"Helvetica"', '"Arial"', 'san-serif'].join(','),
  },
  logo: {
    height: 20,
    width: 20,
    marginRight: theme.spacing(2),
  },
  spinner: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
  },
  authWrapper: {
    // set amplify auth container height 100%
    '& > div:last-of-type': {
      height: '100%',
    },
    paddingTop: 64,
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      padding: 0,
      height: '100%',
    },
  },
  whiteBackground: {
    backgroundColor: Colors.white,
  },
  greyBackground: {
    backgroundColor: Colors.HoverBackground,
  },
  authContainer: {
    [theme.breakpoints.down('xs')]: {
      padding: 0,
      height: '100%',
    },
  },
}));

const excludedNextQueryParams = ['next', 'step', 'email', 'password', 'poolId'];

interface AuthStateData {
  challengeName?: string;
}

const AuthLayout: React.FC = () => {
  const [authState, setAuthState] = useState<string>('');
  const [authStateData, setAuthStateData] = useState<AuthStateData>({});

  const context: ContextProps = useContext(RouteContext);
  const isAutoSignIn = useSelector((state: RootState) => state.ui.isAutoSignIn);

  const { query } = context;
  const { step } = query || '';

  const classes = useStyles();
  const dispatch = useDispatch();

  // Amplify Auth keeps Authenticator constructed and keep reference to this functions
  // so use authenticato reference to check if it is mounted
  const authenticator = React.useRef<HTMLDivElement>();

  const navigate = (path: string) => {
    dispatch(clearAlertMessage());
    history.push(path);
  };

  const authListenerHandler = data => {
    if (
      data &&
      data.payload &&
      data.payload.data &&
      data.payload.data.message
    ) {
      // TODO: temproray solution, change error message
      const authErrorMsg =
        'PreSignUp failed with error A user with the same email address exists.';
      if (data.payload.data.message === authErrorMsg) {
        /* eslint-disable no-param-reassign */
        data.payload.data.message =
          "An account with this email already exists. If you don't remember your password try Forgot Password";
      }

      if (data.payload.event !== 'signIn_failure') {
        dispatch(alertSnackbar({ errorMessage: data.payload.data.message }));
      } else {
        dispatch(
          alertSnackbar({ errorMessage: UserErrorMessage.invalidCredentials }),
        );
      }
    }
  };

  const startAutoSignIn = async (data: AutoSignInProps & AuthStateData) => {
    dispatch(toggleAutoSignIn());
    await autoSignIn({ ...data });
    dispatch(toggleAutoSignIn());
    navigate('/');
  };

  /**
   * This function tracks the auth state
   * and redirects to the appropriate page according to the auth state
   * @param authStateValue: auth state
   * @param data: auth state data
   * @returns
   */
  const handleStateChange = (
    authStateValue: string,
    data: AutoSignInProps & AuthStateData,
  ) => {
    if (!authenticator.current) {
      return;
    }

    if (authStateValue === 'autoSignIn' && data) {
      try {
        startAutoSignIn(data);
      } catch (error) {
        return;
      }
    }

    dispatch(clearAlertMessage());
    setAuthStateData(data);
    setAuthState(authStateValue);

    if (authStateValue && authStateValue === AUTH_STATES.SIGNED_UP && data) {
      Hub.remove('auth', authListenerHandler);
      try {
        startAutoSignIn(data);
      } catch (error) {
        return;
      }
    }

    if (authStateValue && authStateValue === AUTH_STATES.SIGNED_IN) {
      const signedInUserEmail = data.attributes?.email;
      if (
        query.email &&
        signedInUserEmail &&
        query.email.toLowerCase() !== signedInUserEmail.toLowerCase()
      ) {
        // check if email is set in query path and it differs from signed in user email
        logger.debug(
          'Auth login layout loaded for email with different user',
          signedInUserEmail,
          query.email,
        );
        return;
      }
      dispatch(updateUserId(data.username));
      Hub.remove('auth', authListenerHandler);
      let queryString = '';
      if (query && query.next) {
        Object.keys(query).forEach((key: string) => {
          if (!excludedNextQueryParams.includes(key)) {
            queryString = `${queryString ? `${queryString}&` : ''}${key}=${
              query[key]
            }`;
          }
        });
        const queryTail = queryString ? `?${queryString}` : '';
        navigate(`${query.next}${queryTail}`);
      } else {
        const { nextOnboardingStep } = query as OnboardingPageQueryParams;
        if (nextOnboardingStep) {
          const username = `${data.attributes?.given_name} ${data?.attributes?.family_name}`;
          navigate(
            `/onboarding?step=${nextOnboardingStep}&name=${username}&prefill_email=${data.attributes?.email}`,
          );
          return;
        }

        // go to home page if no next query param
        navigate('/');
      }
    }
  };

  // check if username and password properties are provided either from
  // 1. query params: used for login from shared login page to => subdomain
  // 2. session storage: used when login from dashboard page
  const usernamePassProvided =
    // 1. email/password provided in query params
    (query && query.email && (query.password || query.auth)) ||
    (typeof window !== 'undefined' &&
      // 2. email/password provided in session storage as individual items
      window.sessionStorage.getItem('email') &&
      window.sessionStorage.getItem('password')) ||
    (typeof window !== 'undefined' &&
      // 3. email/password provided in session storage as a onboarding credentials
      window.sessionStorage.getItem('onboarding_credentials'));

  let authenticatorChildren = [
    <ConfirmRegisterForm authData={authStateData} key="authConfirmSignUp" />,
  ];
  if (query && step === AUTH_STATES.SIGN_UP) {
    authenticatorChildren.push(<RegisterForm key="authSignUp" />);
  } else if (query && step === AUTH_STATES.FORGOT_PASSWORD) {
    authenticatorChildren.push(<ForgotPasswordForm key="forgotPassword" />);
  } else if (
    usernamePassProvided ||
    authState === AUTH_STATES.REQUIRE_NEW_PASSWORD
  ) {
    authenticatorChildren.push(
      <RequireNewPasswordForm key="authNewPassword" />,
    );
  } else if (
    authState === AUTH_STATES.SIGNED_IN ||
    authState === AUTH_STATES.SIGN_IN
  ) {
    authenticatorChildren.push(<LoginForm key="authSignIn" />);
  }

  useEffect(() => {
    // Listen for the auth errors
    // Errors come inside `data.payload.data.message`
    Hub.listen('auth', authListenerHandler);

    return () => {
      Hub.remove('auth', authListenerHandler);
      authenticatorChildren = [];
    };
  }, []);

  return (
    <Box
      className={`${classes.body} ${
        isAutoSignIn ? classes.whiteBackground : classes.greyBackground
      }`}
    >
      <Container className={classes.authContainer}>
        <Box className={classes.authWrapper}>
          {/**
           * We show this spinner behind all auth steps, to make Authorization UX looks better
           */}
          <Box className={classes.spinner}>
            <LoaderContainer />
          </Box>
          <Authenticator
            ref={authenticator}
            hideDefault
            authState={step}
            onStateChange={handleStateChange}
            authData={authStateData}
            // hide default error notifications
            errorMessage={() => null}
          >
            {authenticatorChildren}
          </Authenticator>
        </Box>
      </Container>
    </Box>
  );
};

export default AuthLayout;
